import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, GraduationCap, X, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const audioUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/lesson-intro-audio/${id}?auth=${localStorage.getItem("tycc_token")}`;

// Banner on the lesson page: plays the pep talks grandparents left for this lesson (auto-tries once per session).
const LessonIntro = ({ child, moduleId }) => {
  const [intros, setIntros] = useState([]);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hidden, setHidden] = useState(false);
  const ref = useRef(null);
  const isServerChild = !!(child?.ownerId || child?.readOnly);
  const sessionKey = `tycc_intro_${child?.id}_${moduleId}`;

  useEffect(() => {
    if (!isServerChild) return;
    setI(0); setHidden(false);
    api.get(`/children/${child.id}/lessons/${moduleId}/intros`).then((r) => setIntros(r.data)).catch(() => setIntros([]));
  }, [child?.id, moduleId, isServerChild]);

  useEffect(() => {
    if (!intros.length || sessionStorage.getItem(sessionKey) === "1") return;
    sessionStorage.setItem(sessionKey, "1");
    ref.current?.play().catch(() => {}); // autoplay may be blocked - the play button is right there
  }, [intros, sessionKey]);

  if (!intros.length || hidden) return null;
  const cur = intros[i];
  const canHeart = !!child?.ownerId && !child.readOnly;
  const toggle = () => (playing ? ref.current.pause() : ref.current.play().catch(() => {}));
  const next = () => { if (i < intros.length - 1) { setI(i + 1); setTimeout(() => ref.current?.play().catch(() => {}), 200); } };
  const heart = async () => {
    try {
      const r = await api.post(`/children/${child.id}/lessons/${moduleId}/intros/${cur.id}/heart`);
      setIntros((l) => l.map((x) => (x.id === cur.id ? { ...x, hearted: r.data.hearted } : x)));
      if (r.data.hearted) toast.success(`${cur.author_name} will know you heard it`, { description: "A little heart goes a long way." });
    } catch { toast.error("Could not send the heart"); }
  };

  return (
    <div className="mb-6 bg-[#7d5fb2] text-white rounded-3xl p-4 sm:p-5 flex items-center gap-4 bubble-in" data-testid="lesson-intro">
      <audio ref={ref} src={audioUrl(cur.id)} preload="auto" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); next(); }} />
      <button type="button" onClick={toggle} className={`w-14 h-14 rounded-full bg-white text-[#7d5fb2] flex items-center justify-center shadow-lg flex-shrink-0 ${playing ? "voice-pulse" : ""}`} aria-label={playing ? "Pause pep talk" : "Play pep talk"} data-testid="lesson-intro-play">{playing ? <Pause size={24} /> : <Play size={24} className="ml-1" />}</button>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-lg leading-tight" style={{ fontFamily: "Literata, serif" }} data-testid="lesson-intro-title"><GraduationCap size={18} className="inline -mt-1 mr-1" /> {cur.author_name} left {child.panda.name} a pep talk</p>
        <p className="text-sm text-white/85">{cur.hearted ? `${child.panda.name} sent a heart back` : "Press play before you start the lesson"}{intros.length > 1 ? ` · ${i + 1} of ${intros.length}` : ""}</p>
      </div>
      {canHeart && <button type="button" onClick={heart} className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${cur.hearted ? "bg-[#e5356f] text-white heart-pop" : "bg-white/20 hover:bg-white/35 text-white"}`} aria-label={cur.hearted ? "Remove heart" : `Send ${cur.author_name} a heart`} title="Tap so they know you heard it" data-testid="lesson-intro-heart" data-hearted={cur.hearted ? "true" : "false"}><Heart size={20} fill={cur.hearted ? "#fff" : "none"} /></button>}
      {intros.length > 1 && i < intros.length - 1 && <button type="button" onClick={next} className="text-xs font-bold bg-white/20 hover:bg-white/35 rounded-full px-3 py-1.5" data-testid="lesson-intro-next">Next</button>}
      <button type="button" onClick={() => setHidden(true)} className="text-white/70 hover:text-white p-1" aria-label="Hide" data-testid="lesson-intro-hide"><X size={18} /></button>
    </div>
  );
};

export default LessonIntro;
